import { useCallback, useState } from "react"
import {
  ReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  Controls,
  type Node,
  type Edge,
  type OnConnect,
  type OnNodesChange,
  type OnEdgesChange,
  Position,
  Panel,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"

const baseStyle = {
  height: "50px",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  paddingLeft: "25px",
  // color: "#39aed2 !important",
}

const nodeStyle = {
  backgroundColor: "#39aed2",
  color: "#39aed2",
  borderColor: "#39aed2",
  ...baseStyle,
}

const nodOfflineStyle = {
  backgroundColor: "#a9a9a9",
  color: "#a9a9a9",
  borderColor: "#a9a9a9",
  ...baseStyle,
}

const nodUrgentStyle = {
  backgroundColor: "#a9a9a9",
  color: "#ff0000",
  borderColor: "#ff0000",
  ...baseStyle,
}

import AssetNode, { AssetType } from "../components/nodes/Asset"
import { HStack, Icon, Text } from "@chakra-ui/react"
import { FaSquare } from "react-icons/fa6"
const nodeTypes = {
  asset: AssetNode,
}
const initialNodes: Node[] = [
  {
    id: "storage",
    sourcePosition: Position.Bottom,
    targetPosition: Position.Right,
    data: { label: "Refuelling bowser", type: AssetType.Storage, id: "bowser" },
    position: { x: 194.0, y: 18.5 },
    style: nodeStyle,
    type: "asset",
  },
  {
    id: "electrolyser",
    sourcePosition: Position.Left,
    targetPosition: Position.Top,
    data: { label: "Storage hub", type: AssetType.Storage, id: "hub" },
    position: { x: 389.5, y: 18.5 },
    style: nodeStyle,
    type: "asset",
  },
  {
    id: "pv",
    sourcePosition: Position.Bottom,
    data: { label: "Hydrogen producer", type: AssetType.Producer },
    position: { x: 389.5, y: -75 },
    style: nodeStyle,
    type: "asset",
  },
  {
    id: "generator",
    data: { label: "Generator", type: AssetType.Generator },
    position: { x: 194.0, y: 132.5 },
    style: nodeStyle,
    type: "asset",
  },
  {
    id: "truck",
    data: { label: "Excavator 1", type: AssetType.Consumer, id: "excavator-1" },
    position: { x: 32.88, y: 132.5 },
    style: nodOfflineStyle,
    type: "asset",
  },
  {
    id: "refueller",
    data: { label: "Excavator 2", type: AssetType.Consumer, id: "excavator-2" },
    position: { x: 356.87, y: 132.5 },
    style: nodUrgentStyle,
    type: "asset",
  },
]

const style = { stroke: "#39aed2", strokeWidth: 2 }
const offlineStyle = { stroke: "#a9a9a9", strokeWidth: 2 }
const initialEdges: Edge[] = [
  {
    id: "pv-electrolyser",
    source: "pv",
    target: "electrolyser",
    animated: true,
    style,
  },
  {
    id: "pv-storage",
    source: "electrolyser",
    target: "storage",
    animated: true,
    style,
  },
  {
    id: "storage-gen",
    source: "storage",
    target: "generator",
    animated: true,
    style,
  },
  {
    id: "storage-truck",
    source: "storage",
    target: "truck",
    animated: false,
    style: offlineStyle,
  },
  {
    id: "storage-refueller",
    source: "storage",
    target: "refueller",
    animated: false,
    style: offlineStyle,
  },
]

const System = () => {
  const [nodes, setNodes] = useState<Node[]>(initialNodes)
  const [edges, setEdges] = useState<Edge[]>(initialEdges)

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  )
  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  )
  const onConnect: OnConnect = useCallback((connection) => setEdges((eds) => addEdge(connection, eds)), [setEdges])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      nodeTypes={nodeTypes}
      connectionLineStyle={style}
      fitView
      style={{ backgroundColor: "#F7F9FB" }}
      nodesDraggable={false}
    >
      <Controls showInteractive={false} position="top-right" />
      <Panel position="bottom-left">
        <HStack ml={"50px"} gap={"8px"}>
          <HStack gap={"3px"}>
            <Icon color={"#39aed2"} as={FaSquare} />
            <Text fontSize={"sm"}>Operational</Text>
          </HStack>
          <HStack gap={"3px"}>
            <Icon color={"#a9a9a9"} as={FaSquare} />
            <Text fontSize={"sm"}>Idle</Text>
          </HStack>
        </HStack>
      </Panel>

      <Background />
    </ReactFlow>
  )
}

export default System
